import { RouterRootState } from "connected-react-router";
import React, { FC, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import AssignedTab from "../../../components/assignWizard/AssignedTab";
import AssignModalTwo from "../../../components/assignWizard/AssignModal";
import { Button } from '../../../components/button/Button';
import { useDialog } from "../../../components/dialog/DialogContext";
import ItemContentTabTwo from "../../../components/item-content-two/item-content-tab_two";
import { Topic } from "../../../components/item-content-two/Topic";
import SimpleItemCard from "../../../components/item-content/SimpleItemCard";
import SimpleItemDetails from "../../../components/item-content/SimpleItemDetails";
import { useLocale } from "../../../contexts/localeContext";
import { TopicContextProvider } from "../../../contexts/topic/TopicContextProvider";
import { TranslationKeys } from "../../../contexts/TranslationKeys";
import { BreadcrumbLink } from "../../../models/BreadcrumbLink";
import { default as EnrolmentColumn, default as EnrolmentColumns } from "../../../models/ColumnDefinitions/EnrolmentColumns";
import { Company as CompanyModel } from "../../../models/Company";
import { ContentWrapper } from "../../../models/ContentWrapper";
import { ListQuery } from "../../../models/ListQuery";
import { Lookup } from "../../../models/Lookup";
import LookupTypes from "../../../models/LookupTypes";
import { MultiSelectOption } from "../../../models/MultiSelectOption";
import { PaginationModel } from "../../../models/Pagination";
import CategorySearch from "../../../models/search/CategorySearch";
import CompanySearch from "../../../models/search/CompanySearch";
import DepartmentSearch from "../../../models/search/DepartmentSearch";
import enrolmentSearchColumns from "../../../models/search/EnrolmentSearch";
import SiteSearch from "../../../models/search/SiteSearch";
import { TabSummary } from "../../../models/TabSummary";
import { hideColumns, hideLookups } from "../../../models/utilities/columnHelper";
import { getCategoryNameWithPagination } from "../../../services/categoryService";
import { getCompanyById, getCompanyNamesWithPagination, storeCompany } from "../../../services/companyService";
import { getDepartmentNamesWithPagination } from "../../../services/departmentService";
import {getEnrolmentNamesWithPagination, getEnrolments} from "../../../services/enrolmentService";
import { getSiteNamesWithPagination } from "../../../services/siteService";
import { getErrorMessage, showError, showLoadItemError } from "../../../services/utilities/ServiceError";
import { IAppState } from "../../../store/types";
import { appendFilter } from "../../../utilities/filters";
import "./personnel.css";
import { Enrolment } from "@/models/Enrolment";
import {batchAssignUnassignCompanyDepartmentRelationship} from "@/services/batchSaveService.ts";

function getIdFromPath(path: string) {
    const splitPath = path.split("/");
    return splitPath[splitPath.length - 1];
}

const itemPath = "/access-control/enrolment/company/";
const allItemsPath = "/access-control/enrolment/companies";

const initialItem: CompanyModel = {
    id: 0,
    name: "",
    site: "",
    siteId: 0,
    default: undefined,
    enrolments: 0,
};

const Company: FC = () => {
    const { translate } = useLocale();
    const initialTabs = [
        {
            label: translate.byKey(TranslationKeys.members),
            badge: 0,
            showInitial: true,
            isActive: false,
        },
    ];
    const history = useHistory();
    const pageId = useSelector((state: RouterRootState) => getIdFromPath(state.router.location.pathname));
    const siteId = useSelector((state: IAppState) => state.site.site.value);
    const [itemId, setItemId] = useState<string>(pageId);
    const [currentItem, setCurrentItem] = useState<CompanyModel>(initialItem);
    const [breadcrumb, setBreadcrumb] = useState<BreadcrumbLink[]>([]);
    const [busy, setBusy] = useState(false);
    const [tabs, setTabs] = useState<TabSummary[]>(initialTabs);
    const [showManagedModal, setShowManagedModal] = useState(false);
    const dialog = useDialog();
    const [selectedItem, setSelectedItem] = useState<MultiSelectOption | undefined>(undefined);
    const lookup: Lookup[] = [
        {
            name: LookupTypes.site,
            values: [],
            dataMethod: getSiteNamesWithPagination,
            searchAccessor: SiteSearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: LookupTypes.company,
            values: [],
            dataMethod: getCompanyNamesWithPagination,
            searchAccessor: CompanySearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: LookupTypes.department,
            values: [],
            dataMethod: getDepartmentNamesWithPagination,
            searchAccessor: DepartmentSearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: LookupTypes.profile_id,
            values: [],
            dataMethod: getCategoryNameWithPagination,
            searchAccessor: CategorySearch.find(item => item.accessor === "name")?.searchOnName
        }
    ]

    const tabContentList: ContentWrapper[] = [
        {
            content: (
                <AssignedTab
                    column={hideLookups(hideColumns(EnrolmentColumns, ['building', 'site']), ["name"])}
                    searchColumn={enrolmentSearchColumns}
                    id={itemId}
                    dataMethod={(listParameters?: ListQuery) =>
                        getEnrolments(translate, {
                            ...listParameters,
                            filters: appendFilter(
                                itemId,
                                "Company",
                                "company.id",
                                listParameters?.filters ?? []
                            ),
                        })
                    }
                    lookup={lookup}
                    title={translate.byKey(TranslationKeys.assignedMembers)}>
                    <Button variant="submit" shape="rounded" className="py-1 px-5" onClick={() => setShowManagedModal(true)}>{translate.byKey(TranslationKeys.manage)}</Button>
                </AssignedTab>
            ),
        },
    ];

    const updateTabBadgeTotal = (tabIndex: number, totalCount: number, currentTabs: TabSummary[]) => {
        const newTabs = [...currentTabs];
        newTabs[tabIndex].badge = totalCount;
        newTabs[tabIndex].isActive = true; // Set tab to active so we can show it
        return newTabs;
    };

    const reloadPage = async () => {
        try {
            let company: any;
            if (itemId) {
                company = await getCompanyById(itemId);

                setSelectedItem({ label: company.name, value: company.id.toString(), isDefault: company.default })
                setCurrentItem(company);

                // update the tab badge counts
                const updatedTabs = updateTabBadgeTotal(
                    0,
                    company?.enrolments,
                    initialTabs
                );
                setTabs(updatedTabs);
            } else {
                setCurrentItem(initialItem);
                setTabs([]);
            }
            setBreadcrumb([
                {
                    value: "/access-control/enrolment",
                    displayName: translate.byKey(TranslationKeys.enrolments),
                },
                {
                    value: allItemsPath,
                    displayName: translate.byKey(TranslationKeys.companies),
                },
                {
                    value: itemPath + `${itemId}`,
                    displayName: `${company?.name ?? ""}`,
                },
            ]);
        } catch (error) {
            showLoadItemError(error, dialog, translate, () => {
                history.push(allItemsPath);
            });
        }
    };

    useEffect(() => {
        setItemId(pageId);
    }, [pageId]);

    useEffect(() => {
        reloadPage();
    }, [itemId]);

    const getTabContent = (index?: number) => {
        // Only return the tab content if the tab is active (clicked an existing record)
        if (tabs[index ?? 0].isActive && itemId) {
            const selectedTabContent = tabContentList[index ?? 0];
            return selectedTabContent.content;
        }
    };

    const saveCompany = (values: any) => {
        const company: CompanyModel = {
            ...currentItem,
            id: +itemId,
            name: values.name,
            siteId: itemId ? values.siteId : siteId,
        };

        const isNew = itemId ? +itemId < 0 : true;
        saveItem(company, isNew);
    };

    const saveItem = (item: CompanyModel, createNewItem: boolean) => {
        setBusy(true);
        storeCompany(item, createNewItem)
            .then(async (value: any) => {
                setCurrentItem(item);
                setItemId(value.id);
                history.push(`${itemPath}${value.id}`);
                toast.success(
                    translate.byKey(TranslationKeys.companySaved)
                );
            })
            .catch((failure) => {
                dialog.pushError({
                    title: translate.byKey(TranslationKeys.error),
                    message: getErrorMessage(failure),
                });
            })
            .finally(() => {
                setBusy(false);
            });
    };

    const topic: Topic = {
        accessor: "compName",
        allItemsPath,
        itemPath,
        title: translate.byKey(TranslationKeys.company),
    };

    const assignMembers = async (toBeAssigned: string[], toBeUnssigned: string[]) => {
        setBusy(true);
        batchAssignUnassignCompanyDepartmentRelationship(toBeAssigned, toBeUnssigned, itemId, "company").then(() => {
            reloadPage();
            toast.success(translate.byKey(TranslationKeys.success));
        })
        .catch((error) => {
            showError(getErrorMessage(error), toast);
        })
        .finally(() => {
            setBusy(false);
        });
    };

    const getAssignedMembers = async (setSelectedIds: React.Dispatch<React.SetStateAction<string[]>>, setSelectedRows: React.Dispatch<React.SetStateAction<any[]>>, setOriginalSelectedIds: React.Dispatch<React.SetStateAction<string[]>>, setOriginalSelectedRows: React.Dispatch<React.SetStateAction<any[]>>, setBusyLoader: React.Dispatch<React.SetStateAction<boolean>>) => {
        try {
            setBusyLoader(true);
            let pageNumber = 1, pagination: PaginationModel, ids: string[] = [], rows: any[] = []
            do {
                const enrolments = await getEnrolmentNamesWithPagination({ filters: itemId ? appendFilter(itemId, "Company", "company.id", []) : [], pageSize: 100, goToPage: pageNumber });
                ids = ids.concat(enrolments.rows.map(row => row.value))
                rows = rows.concat(enrolments.rows.map(row => ({ id: row.value, name: row.label })));
                pagination = enrolments.pagination;
                pageNumber++;
            } while (pageNumber <= pagination.totalPages);
            setSelectedIds(ids);
            setSelectedRows(rows)
            setOriginalSelectedIds(ids);
            setOriginalSelectedRows(rows);
        } catch (error) {
            toast.warn(getErrorMessage(error));
        }
        finally {
            setBusyLoader(false);
        }
    }

    return (
        <div className="flex h-full">
            <TopicContextProvider
                dataMethod={getCompanyNamesWithPagination}
                itemId={itemId}
                topic={topic}
                selectedItem={selectedItem}>
                <ItemContentTabTwo>
                    <SimpleItemDetails
                        breadcrumb={breadcrumb}
                        busy={busy}
                        getTabContent={getTabContent}
                        itemProfile={currentItem}
                        onBack={() => history.push(allItemsPath)}
                        saveDetails={saveCompany}
                        tabs={tabs}
                        itemPath={itemPath}
                        initialValues={currentItem}
                        children={<SimpleItemCard />} />
                </ItemContentTabTwo>
            </TopicContextProvider>
            <AssignModalTwo
                key={`assignModalForCompany${itemId}`}
                tableColumns={hideColumns(EnrolmentColumn, ["totalTags", "category"])}
                searchColumns={enrolmentSearchColumns}
                isOpen={showManagedModal}
                onClose={() => setShowManagedModal(false)}
                assigningTitle={translate.byKey(TranslationKeys.assignMembers)}
                modalTitle={translate.byKey(TranslationKeys.members)}
                lookup={lookup}
                getAssignedItems={getAssignedMembers}
                dataMethod={(listParameter?: ListQuery) => getEnrolments(translate, listParameter)}
                handleSubmit={assignMembers} />
        </div>
    );
};

export default Company;
